import { PerformInspectionButton } from "@/components/registrant/dashboard/perform-inspection-button";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { UserType } from "@/constants";
import type {
  LicenseResponseSchema,
  QuestionResponseSchema,
} from "@/hera-api/client/types.gen";
import { useRegistrantOrgPermissions } from "@/hooks/use-registrant-org-permissions";
import { isServiceTransferDeregistration } from "@/lib/service-transfer-deregistration";
import {
  getLicenseActionLink,
  isCancelableForOperatorOrGovernment,
  isCancelableForRegistrant,
  isDeregisterable,
  isPayable,
  isRejected,
  isRenewable,
  isReviewable,
  isTransferable,
  newestInspectionForListStatus,
} from "@/lib/utils";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  DollarSign,
  Eye,
  Link2,
  LogOut,
  MoveRight,
  RefreshCw,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { InspectionPerformQr } from "./inspection-perform-qr";
import { LinkLicenseDialog } from "./link-license-dialog";

/** Tooltip messages when an action is disabled (title: reason). */
const DISABLED_REASONS = {
  pay: "Payment Not Available: Payment is only available when there are existing amounts that were never paid or where the payment failed.",
  renew:
    "Renewal Not Available: Renewals can only be made when the registration is not in process and is either expired or within 30 days of the renewal date.",
  transfer:
    "Transfer Not Available: Transfers can only be made when the registration is compliant or has a balance due. Registrations awaiting payment confirmation or review, non-compliant, exempt, cancelled, rejected, or already de-registering cannot be transferred.",
  linkLicense:
    "Link Not Available: Linking requires a property and cannot run while a payment is processing.",
  cancelRegistrant:
    "Cancel Not Available: Cancellation is only available when the license is in Balance Due or Payment Failed status.",
  cancelOperatorGov:
    "Cancel Not Available: Cancellation is not available for REJECTED, CANCELLED, or PAYMENT_PROCESSING licenses.",
  approve:
    "Approval Not Available: This license is not in a status that allows review and approval.",
  reject:
    "Rejection Not Available: This license is not in a status that allows review and rejection.",
} as const;

interface ActionsCardContentProps {
  data: LicenseResponseSchema;
  userType: UserType;
  showApproveDialog: (licenseId: string) => void;
  showRejectDialog: (licenseId: string) => void;
  /** Deregistration question defs (for detecting service-transfer deregistrations). */
  deregistrationQuestions?: QuestionResponseSchema[];
}

export function ActionsCardContent({
  data,
  userType,
  showApproveDialog,
  showRejectDialog,
  deregistrationQuestions,
}: ActionsCardContentProps) {
  const navigate = useNavigate();
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const isRejectedLicense = isRejected(data);
  const canLinkLicense =
    data.property != null && data.status !== "PAYMENT_PROCESSING";

  const isServiceTransferPending =
    data.status === "DE_REGISTRATION_PENDING" &&
    isServiceTransferDeregistration(
      deregistrationQuestions,
      data.answers_to_additional_questions,
    );

  const canApprove = isReviewable(data) && !isServiceTransferPending;
  const canReject = isReviewable(data);
  const canTransfer = isTransferable(data) || isServiceTransferPending;
  const canCancelRegistrant = isCancelableForRegistrant(data.status);
  const canCancelOperatorGov = isCancelableForOperatorOrGovernment(data.status);
  const canPay = isPayable(data);
  const inspectionPending =
    newestInspectionForListStatus(data.inspections)?.status ===
    "INSPECTION_PENDING";
  const { canMakePayments } = useRegistrantOrgPermissions();
  // Registrants without the pay grant still get in: the payment screen offers
  // them Pay Later only.
  const payLabel =
    userType === UserType.REGISTRANT && !canMakePayments
      ? "Pay Later"
      : "Pay Now";

  const goToTransfer = () => {
    // Transfer UI lives under operator routes (government has no /transfer page).
    navigate({
      to: "/operator/licenses/$licenseId/transfer",
      params: { licenseId: data.id },
      search: isServiceTransferPending ? { fromDeregistration: true } : {},
    });
  };

  return (
    <CardContent>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(userType === UserType.OPERATOR ||
          userType === UserType.GOVERNMENT) && (
          <>
            {isServiceTransferPending && userType === UserType.OPERATOR ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full min-w-[80px]"
                disabled={!canTransfer}
                title={!canTransfer ? DISABLED_REASONS.transfer : undefined}
                onClick={goToTransfer}
              >
                <MoveRight className="h-4 w-4" />
                Transfer
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                className="w-full min-w-[80px]"
                disabled={!canApprove}
                title={!canApprove ? DISABLED_REASONS.approve : undefined}
                onClick={() => showApproveDialog(data.id)}
              >
                <ThumbsUp className="h-4 w-4" />
                {data.status === "DE_REGISTRATION_PENDING"
                  ? "Approve De-registration..."
                  : "Approve Registration..."}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="w-full min-w-[80px]"
              disabled={!canReject}
              title={!canReject ? DISABLED_REASONS.reject : undefined}
              onClick={() => showRejectDialog(data.id)}
            >
              <ThumbsDown className="h-4 w-4" />
              {data.status === "DE_REGISTRATION_PENDING"
                ? "Reject De-registration..."
                : "Reject Registration..."}
            </Button>
          </>
        )}
        {userType === UserType.REGISTRANT && (
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <Button
              variant="outline"
              size="sm"
              asChild
              className="flex-1 sm:flex-none min-w-[80px]"
            >
              <Link to={getLicenseActionLink(userType, "view", data.id)}>
                <Eye className="h-4 w-4" />
                <span className="ml-1">View</span>
              </Link>
            </Button>
            {isRenewable(data) && !isRejectedLicense && (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="flex-1 sm:flex-none min-w-[80px]"
              >
                <Link to={getLicenseActionLink(userType, "renew", data.id)}>
                  <RefreshCw className="h-4 w-4" />
                  <span className="ml-1">Renew</span>
                </Link>
              </Button>
            )}
            {isRejectedLicense && (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="flex-1 sm:flex-none min-w-[80px]"
              >
                <Link to={getLicenseActionLink(userType, "renew", data.id)}>
                  <RotateCcw className="h-4 w-4" />
                  <span className="ml-1">Reapply</span>
                </Link>
              </Button>
            )}
            {canPay && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-none min-w-[80px]"
                onClick={() =>
                  navigate({
                    to: "/registrant/licenses/$licenseId/payment",
                    params: { licenseId: data.id },
                    // Ignored without the pay grant: that form hides the picker.
                    search: { paymentMethod: "card" },
                  })
                }
              >
                <DollarSign className="h-4 w-4" />
                <span className="ml-1">{payLabel}</span>
              </Button>
            )}
            {isDeregisterable(data) && (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="flex-1 sm:flex-none min-w-[80px]"
              >
                <Link
                  to={getLicenseActionLink(userType, "deregister", data.id)}
                >
                  <LogOut className="h-4 w-4" />
                  <span className="ml-1">De-register</span>
                </Link>
              </Button>
            )}
            {canCancelRegistrant && (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="flex-1 sm:flex-none min-w-[80px] text-destructive hover:bg-destructive hover:text-destructive-foreground"
              >
                <Link to={getLicenseActionLink(userType, "cancel", data.id)}>
                  <Trash2 className="h-4 w-4" />
                  <span className="ml-1">Cancel</span>
                </Link>
              </Button>
            )}
          </div>
        )}
        {(userType === UserType.OPERATOR ||
          userType === UserType.GOVERNMENT) && (
          <>
            {userType === UserType.OPERATOR && !isServiceTransferPending && (
              <Button
                variant="outline"
                size="sm"
                className="w-full min-w-[80px]"
                disabled={!canTransfer}
                title={!canTransfer ? DISABLED_REASONS.transfer : undefined}
                onClick={goToTransfer}
              >
                <MoveRight className="h-4 w-4" />
                <span className="ml-1">Transfer</span>
              </Button>
            )}
            {userType === UserType.OPERATOR && (
              <Button
                variant="outline"
                size="sm"
                className="w-full min-w-[80px]"
                disabled={!canLinkLicense}
                title={
                  !canLinkLicense ? DISABLED_REASONS.linkLicense : undefined
                }
                onClick={() => setIsLinkDialogOpen(true)}
              >
                <Link2 className="h-4 w-4" />
                <span className="ml-1">Link License...</span>
              </Button>
            )}
            {userType === UserType.OPERATOR && isDeregisterable(data) && (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="w-full min-w-[80px]"
              >
                <Link
                  to={getLicenseActionLink(userType, "deregister", data.id)}
                >
                  <LogOut className="h-4 w-4" />
                  <span className="ml-1">De-register</span>
                </Link>
              </Button>
            )}
            {userType === UserType.OPERATOR && (
              <Button
                variant="outline"
                size="sm"
                className="w-full min-w-[80px] text-destructive hover:bg-destructive hover:text-destructive-foreground"
                disabled={!canCancelOperatorGov}
                title={
                  !canCancelOperatorGov
                    ? DISABLED_REASONS.cancelOperatorGov
                    : undefined
                }
                onClick={() =>
                  navigate({
                    to: getLicenseActionLink(userType, "cancel", data.id),
                  })
                }
              >
                <Trash2 className="h-4 w-4" />
                <span className="ml-1">Cancel</span>
              </Button>
            )}
          </>
        )}
        {inspectionPending ? (
          <div className="sm:col-span-2 flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center">
            <PerformInspectionButton
              licenseId={data.id}
              className="w-full sm:w-auto"
            />
            <InspectionPerformQr licenseId={data.id} />
          </div>
        ) : null}
      </div>
      {userType === UserType.OPERATOR ? (
        <LinkLicenseDialog
          isOpen={isLinkDialogOpen}
          onOpenChange={setIsLinkDialogOpen}
          licenseId={data.id}
        />
      ) : null}
    </CardContent>
  );
}
